const ROOT_FONT_SIZE = 16

const pixelsToRems = (pixels, includeUnit = true) => {
  const rems = pixels / ROOT_FONT_SIZE

  return `${rems}${includeUnit ? 'rem' : ''}`
}

const lineHeight = (fontSizePixels, lineHeightPixels) =>
  lineHeightPixels / fontSizePixels

const clamp = ({
  minPixels,
  maxPixels,
  minViewportWidthPixels = 400,
  maxViewportWidthPixels = 2000,
}) => {
  const minValueRems = pixelsToRems(minPixels, false)
  const maxValueRems = pixelsToRems(maxPixels, false)
  const minViewportWidthRems = pixelsToRems(minViewportWidthPixels, false)
  const maxViewportWidthRems = pixelsToRems(maxViewportWidthPixels, false)

  const slope =
    (maxValueRems - minValueRems) /
    (maxViewportWidthRems - minViewportWidthRems)

  const intercept = minValueRems - slope * minViewportWidthRems

  return `clamp(${minValueRems}rem, ${intercept}rem + ${
    slope * 100
  }vw, ${maxValueRems}rem)`
}

export default {
  content: ['./src/**/*.{astro,html,tsx}'],
  safelist: [
    {
      pattern: /^delay-[0-9]+/,
    },
  ],
  theme: {
    colors: {
      transparent: 'transparent',
      black: '#000000',
      'black-15': 'rgba(0, 0, 0, 0.15)',
      'black-25': 'rgba(0, 0, 0, 0.25)',
      'black-30': 'rgba(0, 0, 0, 0.30)',
      'black-50': 'rgba(0, 0, 0, 0.50)',
      'black-75': 'rgba(0, 0, 0, 0.75)',
      grey: '#D9D9D9',
      'grey-dark': '#808080',
      'grey-darker': '#404040',
      white: '#FFFFFF',
      'white-15': 'rgba(255, 255, 255, 0.15)',
      'white-25': 'rgba(255, 255, 255, 0.25)',
      'white-50': 'rgba(255, 255, 255, 0.50)',
      'white-75': 'rgba(255, 255, 255, 0.75)',
      milk: '#EBEBE4',
      'milk-light': '#F5F5F1',
      'milk-lighter': '#FAFAF8',
      lime: '#5BAB5D',
      kiwi: '#CAD556',
      mint: '#AECEA7',
      pistachio: '#DFE3AE',
      tomato: '#FF6341',
      tangerine: '#F89D3E',
      daffodil: '#EDE15B',
      blush: '#E8C8A4',
      eggplant: '#35252C',
      red: '#EC413A',
    },
    spacing: {
      0: '0px',
      4: '4px',
      8: '8px',
      12: '12px',
      14: '14px',
      16: '16px',
      20: '20px',
      24: '24px',
      32: '32px',
      40: '40px',
      48: '48px',
      56: '56px',
      64: '64px',
      80: '80px',
      128: '128px',
      160: '160px',
      240: '240px',
    },
    height: {
      headerMobile: '64px',
      headerDesktop: '96px',
      notificationMobile: '60px',
      notificationDesktop: '30px',
      navigationDesktop: '300px',
      navigationDesktopMax: '500px',
    },
    maxWidth: {
      text: '65ch',
      form: '480px',
    },
    minWidth: {
      form: '311px',
    },
    fontFamily: {
      heading: ['Sakara', 'serif'],
      body: ['Triptych', 'serif'],
      label: ['"Rework Micro"', 'sans-serif'],
      eyebrow: ['"Rework Micro"', 'sans-serif'],
    },
    fontSize: {
      label: pixelsToRems(11),
      eyebrow: pixelsToRems(12),
      'body-sm': pixelsToRems(12),
      body: pixelsToRems(14),
      'heading-3xs': pixelsToRems(20),
      'heading-2xs': pixelsToRems(24),
      'heading-xs': pixelsToRems(28),
      'heading-sm': pixelsToRems(32),
      'heading-md': pixelsToRems(36),
      'heading-lg': pixelsToRems(44),
      'heading-xl': pixelsToRems(48),
      'heading-2xl': pixelsToRems(64),
      'heading-3xl': pixelsToRems(80),
      'responsive-sm': clamp({
        minPixels: 44,
        maxPixels: 127,
        minViewportWidthPixels: 375,
        maxViewportWidthPixels: 1023,
      }),
      'responsive-md': clamp({
        minPixels: 44,
        maxPixels: 133,
        minViewportWidthPixels: 1024,
        maxViewportWidthPixels: 2128,
      }),
    },
    lineHeight: {
      1: 1,
      label: lineHeight(11, 14),
      eyebrow: lineHeight(12, 14),
      body: lineHeight(14, 22),
      'heading-3xl': lineHeight(80, 74),
    },
    letterSpacing: {
      label: '0.06em',
      eyebrow: '0.06em',
    },
    aspectRatio: {
      '1/1': '1 / 1',
      '5/4': '5 / 4',
      '16/9': '16 / 9',
      '4/5': '4 / 5',
      '2/3': '2 / 3',
      '3/4': '3 / 4',
      '4/3': '4 / 3',
    },
    underlineOffset: {
      4: '4px',
    },
    outlineOffset: {
      4: '4px',
    },
    transitionTimingFunction: {
      smooth: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
      in: 'cubic-bezier(0.4, 0.0, 1, 1)',
      out: 'cubic-bezier(0.0, 0.0, 0.2, 1)',
      outQuart: 'cubic-bezier(0.165, 0.84, 0.44, 1)',
      outExpo: 'cubic-bezier(0.19, 1, 0.22, 1)',
    },
    screens: {
      smMax: { max: '639px' },
      sm: '640px',
      mdMax: { max: '767px' },
      md: '768px',
      lgMax: { max: '1024px' },
      lg: '1025px',
      xlMax: { max: '1199px' },
      xl: '1200px',
      collectionLinksNoWrap: '1216px',
      xlExtendedMax: { max: '1240px' },
      '2xlMax': { max: '1535px' },
      '2xl': '1536px',
      max: '2000px',
    },
    extend: {
      opacity: {
        15: '0.15',
      },
      transitionDuration: {
        250: '250ms',
        350: '350ms',
        400: '400ms',
        750: '750ms',
        800: '800ms',
        1500: '1500ms',
      },
      transitionDelay: {
        900: '900ms',
      },
      zIndex: {
        '-1': '-1',
        1: '1',
        10: '10',
        40: '40',
        50: '50',
        100: '100',
      },
    },
  },
}
